/**
 * V2 source path: customer/midas/components/v2/MessageActionsV2.tsx
 * V1 source: scratch (greenfield — V1 had no terminal action row)
 * Strategy: scratch-with-reused-handlers
 * Verified no-MUI: yes
 * Phase: 1
 *
 * Reused handlers / state:
 *   - `navigator.clipboard.writeText` for Copy
 *   - `midasMessageFeedbackV2Slice` (Phase 1 local-only — Phase 5 wires backend)
 *
 * Renders ALWAYS at the END of an assistant turn (Round 6 placement rule —
 * after the last content block, including cited-from row). Touch-first sizing
 * (44×44 hit) on phone, compact (24×24) on desktop per Paper artboards.
 */
import React, { useCallback, useState } from "react";
import { Copy, ThumbsUp, ThumbsDown, RotateCcw, Check } from "lucide-react";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import {
  toggleLike,
  toggleDislike,
  requestRetry,
} from "./midasMessageFeedbackV2Slice";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "./ui/tooltip";

interface MessageActionsV2Props {
  messageId: string;
  text: string;
  isPhone?: boolean;
}

const MessageActionsV2: React.FC<MessageActionsV2Props> = ({
  messageId,
  text,
  isPhone = false,
}) => {
  const dispatch = useAppDispatch();
  const feedback = useAppSelector(
    (s) => s.midasMessageFeedbackV2.byMessageId[messageId]
  );
  const liked = feedback?.liked || false;
  const disliked = feedback?.disliked || false;
  const retryRequested = feedback?.retryRequested || false;

  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // clipboard.writeText can reject in non-secure contexts; ignore silently.
    }
  }, [text]);

  const handleLike = useCallback(() => {
    dispatch(toggleLike({ messageId }));
  }, [dispatch, messageId]);

  const handleDislike = useCallback(() => {
    dispatch(toggleDislike({ messageId }));
  }, [dispatch, messageId]);

  const handleRetry = useCallback(() => {
    dispatch(requestRetry({ messageId }));
  }, [dispatch, messageId]);

  // Touch hit target: 44×44 on phone, 24×24 desktop (per Paper artboards).
  // Visual icon stays 16px on both.
  const btnSize = isPhone ? "h-11 w-11" : "h-7 w-7";
  const iconSize = "h-4 w-4";

  return (
    <div className="mt-3 flex items-center justify-between gap-2">
      <div className="flex items-center gap-1">
        {/* Copy */}
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={handleCopy}
              aria-label="Copy reply"
              className={`inline-flex ${btnSize} items-center justify-center rounded-md text-midas-warm-graphite transition-colors hover:bg-midas-terracotta-soft hover:text-midas-terracotta active:scale-[0.96]`}
            >
              {copied ? (
                <Check className={iconSize} strokeWidth={1.75} />
              ) : (
                <Copy className={iconSize} strokeWidth={1.75} />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <span className="font-mono text-[11px]">
              {copied ? "Copied" : "Copy"}
            </span>
          </TooltipContent>
        </Tooltip>

        {/* Like */}
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={handleLike}
              aria-label="Like reply"
              aria-pressed={liked}
              className={`inline-flex ${btnSize} items-center justify-center rounded-md transition-colors active:scale-[0.96] ${
                liked
                  ? "bg-midas-terracotta-soft text-midas-terracotta"
                  : "text-midas-warm-graphite hover:bg-midas-terracotta-soft hover:text-midas-terracotta"
              }`}
            >
              <ThumbsUp
                className={iconSize}
                strokeWidth={liked ? 2 : 1.75}
                {...(liked ? { fill: "currentColor" } : {})}
              />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <span className="font-mono text-[11px]">Like</span>
          </TooltipContent>
        </Tooltip>

        {/* Dislike */}
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={handleDislike}
              aria-label="Dislike reply"
              aria-pressed={disliked}
              className={`inline-flex ${btnSize} items-center justify-center rounded-md transition-colors active:scale-[0.96] ${
                disliked
                  ? "bg-midas-terracotta-soft text-midas-terracotta"
                  : "text-midas-warm-graphite hover:bg-midas-terracotta-soft hover:text-midas-terracotta"
              }`}
            >
              <ThumbsDown
                className={iconSize}
                strokeWidth={disliked ? 2 : 1.75}
                {...(disliked ? { fill: "currentColor" } : {})}
              />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <span className="font-mono text-[11px]">Dislike</span>
          </TooltipContent>
        </Tooltip>

        {/* Retry */}
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={handleRetry}
              aria-label="Retry reply"
              aria-pressed={retryRequested}
              className={`inline-flex ${btnSize} items-center justify-center rounded-md transition-colors active:scale-[0.96] ${
                retryRequested
                  ? "bg-midas-terracotta-soft text-midas-terracotta"
                  : "text-midas-warm-graphite hover:bg-midas-terracotta-soft hover:text-midas-terracotta"
              }`}
            >
              <RotateCcw className={iconSize} strokeWidth={1.75} />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <span className="font-mono text-[11px]">Retry</span>
          </TooltipContent>
        </Tooltip>
      </div>

      <span className="font-mono text-[10px] text-midas-muted-stone">
        helps tune midas
      </span>
    </div>
  );
};

export default MessageActionsV2;
